import { useCallback, useLayoutEffect, useRef } from 'react'
import {
  Button,
  Collapse,
  DoubleArrow,
  Expand,
  Fullscreen,
  LToRIcon,
  MenuOpen,
  RToLIcon,
} from '../../primitives/index.js'
import { collapseAll, expandAll, expandToSlug, useTreeApi } from '../atoms/treeState.js'
import type { UpdateFunction } from '../hooks/useQueryState/useQueryState.js'
import { useStoryRoute } from '../hooks/useStoryRoute.js'

export type Props = {
  sidebarPosition: 'row' | 'row-reverse'
  setCollapsed: UpdateFunction<boolean>
  setFullScreen: UpdateFunction<boolean>
  onChangeSidebarPosition: UpdateFunction<'row' | 'row-reverse'>
}

export function NavigationControls(props: Props) {
  const { setCollapsed, setFullScreen, sidebarPosition, onChangeSidebarPosition } = props

  const _treeApi = useTreeApi()

  const rSidebarPosition = useRef(sidebarPosition)
  useLayoutEffect(() => void (rSidebarPosition.current = sidebarPosition), [sidebarPosition])

  const handleSidebarPosition = useCallback(() => {
    onChangeSidebarPosition(rSidebarPosition.current === 'row' ? 'row-reverse' : 'row')
  }, [onChangeSidebarPosition])

  const handleEnterFullScreen = useCallback(() => {
    setFullScreen(true)
  }, [setFullScreen])

  const handleCollapseSidebar = useCallback(() => {
    setCollapsed(true)
  }, [setCollapsed])

  const { story } = useStoryRoute()
  const onClickGoToStory = useCallback(() => {
    expandToSlug(story)
  }, [story])

  return (
    <div
      style={{
        alignItems: 'center',
        display: 'flex',
        flex: 0,
        flexDirection: sidebarPosition,
        justifyContent: 'space-between',
        marginBottom: '8px',
      }}
    >
      <span
        data-tooltip="Collapse Sidebar"
        data-tooltip-position={sidebarPosition === 'row' ? 'bottom-left' : 'bottom-right'}
      >
        <Button onClick={handleCollapseSidebar}>
          <DoubleArrow
            style={{
              transform: sidebarPosition === 'row' ? 'scale(-1, 1)' : 'scale(1, 1)',
            }}
          />
        </Button>
      </span>

      <span data-tooltip="Toggle full screen" data-tooltip-position="bottom">
        <Button onClick={handleEnterFullScreen}>
          <Fullscreen />
        </Button>
      </span>

      <span data-tooltip="Collapse" data-tooltip-position="bottom">
        <Button onClick={collapseAll}>
          <Collapse />
        </Button>
      </span>

      <span data-tooltip="Expand" data-tooltip-position="bottom">
        <Button onClick={expandAll}>
          <Expand />
        </Button>
      </span>

      <span data-tooltip="Go to current story" data-tooltip-position="bottom">
        <Button onClick={onClickGoToStory}>
          <MenuOpen />
        </Button>
      </span>

      <span
        data-tooltip="Change sidebar position"
        data-tooltip-position={sidebarPosition === 'row' ? 'bottom-right' : 'bottom-left'}
      >
        <Button onClick={handleSidebarPosition}>{sidebarPosition === 'row' ? <LToRIcon /> : <RToLIcon />}</Button>
      </span>
    </div>
  )
}
